/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { jsonWebKeySetsRevokeKey } from "../funcs/jsonWebKeySetsRevokeKey.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { JSONWebKey } from "../models/components/jsonwebkey.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevokeJsonWebKeyRequest,
  RevokeJsonWebKeySecurity,
} from "../models/operations/revokejsonwebkey.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RevokeJsonWebKeyMutationVariables = {
  request: RevokeJsonWebKeyRequest;
  security?: RevokeJsonWebKeySecurity | undefined;
  options?: RequestOptions;
};

export type RevokeJsonWebKeyMutationData = JSONWebKey;

export type RevokeJsonWebKeyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revokeKey jsonWebKeySets
 *
 * @remarks
 * Withdraw a published key entirely: it leaves the published set and tokens signed with it stop verifying. This is the compromise response, not the graceful wind-down — use retire for that. A revoked kid can never be republished into the set. Requires org:admin.
 */
export function useRevokeJsonWebKeyMutation(
  options?: MutationHookOptions<
    RevokeJsonWebKeyMutationData,
    RevokeJsonWebKeyMutationError,
    RevokeJsonWebKeyMutationVariables
  >,
): UseMutationResult<
  RevokeJsonWebKeyMutationData,
  RevokeJsonWebKeyMutationError,
  RevokeJsonWebKeyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRevokeJsonWebKeyMutation(client, options),
    ...options,
  });
}

export function mutationKeyRevokeJsonWebKey(): MutationKey {
  return ["@gram/client", "jsonWebKeySets", "revokeKey"];
}

export function buildRevokeJsonWebKeyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RevokeJsonWebKeyMutationVariables,
  ) => Promise<RevokeJsonWebKeyMutationData>;
} {
  return {
    mutationKey: mutationKeyRevokeJsonWebKey(),
    mutationFn: function revokeJsonWebKeyMutationFn({
      request,
      security,
      options,
    }): Promise<RevokeJsonWebKeyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(jsonWebKeySetsRevokeKey(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
